class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('lkp-details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.overlay = document.querySelector('.lkp-mega-overlay');
    this.navRoot = this.closest('.header__inline-menu');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.content = this.mainDetailsToggle.querySelector('.mega-menu__content');
    HeaderMenu.ensureGlobalMetricsListener();

    // Hover intent: keep menu open while pointer stays inside nav; switch when hovering another item.
    this.pointerEnterHandler = this.handlePointerEnter.bind(this);

    // Open when hovering the trigger or the panel itself
    this.summary?.addEventListener('mouseenter', this.pointerEnterHandler);
    this.content?.addEventListener('mouseenter', this.pointerEnterHandler);
    this.mainDetailsToggle.addEventListener('mouseenter', this.pointerEnterHandler);

    // Shared close handling when leaving the entire nav region
    if (this.navRoot && !this.navRoot._lkpHoverBound) {
      this.navRoot.addEventListener('mouseenter', HeaderMenu.cancelScheduledClose);
      this.navRoot.addEventListener('mouseleave', HeaderMenu.scheduleCloseAll);
      this.navRoot._lkpHoverBound = true;
    }

    // Click on overlay closes any open mega menu; guard so we bind only once.
    if (this.overlay && !this.overlay._lkpBound) {
      this.overlay.addEventListener('click', () => {
        document.querySelectorAll('lkp-header-menu details[open]').forEach((menu) => {
          const summary = menu.querySelector('summary');
          menu.removeAttribute('open');
          summary && summary.setAttribute('aria-expanded', false);
        });
        this.toggleOverlay(false);
      });
      this.overlay._lkpBound = true;
    }

    // Keep header metrics in sync with dynamic announcement bar height changes
    // so the overlay always starts exactly under the visible header.
    HeaderMenu.requestHeaderMetricsSync();
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    // Sync overlay visibility with open state
    this.toggleOverlay(this.mainDetailsToggle.open);
    // Refresh custom property every time the menu toggles so changes in
    // header/announcement bar height are reflected immediately.
    HeaderMenu.requestHeaderMetricsSync();
  }

  handlePointerEnter() {
    HeaderMenu.cancelScheduledClose();
    HeaderMenu.openMenu(this.mainDetailsToggle);
  }

  static openMenu(targetDetails) {
    if (!targetDetails) return;

    document.querySelectorAll('lkp-header-menu details[open]').forEach((menu) => {
      if (menu !== targetDetails) {
        menu.open = false;
        const summary = menu.querySelector('summary');
        summary && summary.setAttribute('aria-expanded', false);
      }
    });

    if (!targetDetails.open) {
      targetDetails.open = true;
      const summary = targetDetails.querySelector('summary');
      summary && summary.setAttribute('aria-expanded', true);
    }
  }

  static scheduleCloseAll() {
    HeaderMenu.cancelScheduledClose();
    HeaderMenu.closeTimeout = window.setTimeout(() => {
      document.querySelectorAll('lkp-header-menu details[open]').forEach((menu) => {
        menu.open = false;
        const summary = menu.querySelector('summary');
        summary && summary.setAttribute('aria-expanded', false);
      });

      const overlay = document.querySelector('.lkp-mega-overlay');
      if (overlay) {
        overlay.classList.remove('is-visible');
        overlay.setAttribute('hidden', 'hidden');
      }
    }, 120);
  }

  static cancelScheduledClose() {
    if (HeaderMenu.closeTimeout) {
      window.clearTimeout(HeaderMenu.closeTimeout);
      HeaderMenu.closeTimeout = null;
    }
  }

  toggleOverlay(isOpen) {
    if (!this.overlay) return;
    if (isOpen) {
      this.overlay.classList.add('is-visible');
      this.overlay.removeAttribute('hidden');
    } else {
      const anyOpen = document.querySelector('lkp-header-menu details[open]');
      if (!anyOpen) {
        this.overlay.classList.remove('is-visible');
        this.overlay.setAttribute('hidden', 'hidden');
      }
    }
  }

  static ensureGlobalMetricsListener() {
    if (HeaderMenu._metricsBound) return;
    HeaderMenu._metricsBound = true;
    HeaderMenu._boundMetricsHandler = HeaderMenu.requestHeaderMetricsSync;
    window.addEventListener('resize', HeaderMenu._boundMetricsHandler, { passive: true });
    window.addEventListener('scroll', HeaderMenu._boundMetricsHandler, { passive: true });
  }

  static requestHeaderMetricsSync() {
    if (HeaderMenu._metricsFrame) return;

    HeaderMenu._metricsFrame = window.requestAnimationFrame(() => {
      HeaderMenu._metricsFrame = null;
      HeaderMenu.syncHeaderMetrics();
    });
  }

  static syncHeaderMetrics() {
    const headerEl =
      document.querySelector('.section-header') ||
      document.querySelector('.header-wrapper');
    if (!headerEl) return;

    const borderOffset = headerEl.classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    const bottom = headerEl.getBoundingClientRect().bottom - borderOffset;
    const value = `${Math.round(bottom)}px`;

    if (HeaderMenu._lastHeaderBottom === value) return;
    HeaderMenu._lastHeaderBottom = value;
    document.documentElement.style.setProperty('--header-bottom-position-desktop', value);
  }
}

customElements.define('lkp-header-menu', HeaderMenu);
